import ExcelJS from 'exceljs'
import { saveAs } from 'file-saver'
import moment from 'moment'

export interface PatientDateOfBirthExportData {
  cnNumber: string;
  snCode?: string;
  fullName: string;
  gender: string;
  dateOfBirthBuddhist: string;
  age: number;
  phone: string;
  telephone: string;
  line: string;
  email: string;
  facebook: string;
  branchName: string;
  createdAtBuddhist: string;
}

export interface ExportToExcelProps {
  data: PatientDateOfBirthExportData[];
  filter: string[];
  snType: boolean;
  t: any;
}

async function ExportToExcel({
  data,
  filter,
  snType,
  t,
}: ExportToExcelProps) {
  const workbook = new ExcelJS.Workbook()
  const sheet = workbook.addWorksheet(t('REPORT.REPORT_NAME.PATIENT_DATE_OF_BIRTH'))

  // เพิ่มหัวข้อรายงาน
  const titleRow = sheet.addRow([t('REPORT.REPORT_NAME.PATIENT_DATE_OF_BIRTH')])
  titleRow.font = { bold: true, size: 16 }
  sheet.mergeCells(titleRow.number, 1, titleRow.number, snType ? 13 : 12)
  titleRow.alignment = { horizontal: 'left' }

  // เพิ่มข้อมูลตัวกรอง
  const filterLabels = [
    t('REPORT.FILTER.BRANCH'),
    t('REPORT.MONTH.DATE_OF_BIRTH'),
  ]
  const filterValues = [
    filter[1] || '-',
    filter[2] || '-',
  ]

  const filterRowData: any[] = []
  for (let i = 0; i < filterLabels.length; i++) {
    filterRowData.push(filterLabels[i] + ':', filterValues[i])
  }
  const filterRow = sheet.addRow(filterRowData)
  filterRow.font = { size: 11 }
  for (let col = 1; col <= filterRowData.length; col += 2) {
    filterRow.getCell(col).font = { bold: true }
    filterRow.getCell(col).alignment = { horizontal: 'left' }
    filterRow.getCell(col + 1).alignment = { horizontal: 'left' }
  }

  // กำหนดหัวตาราง
  const headCells = [
    { label: t('REPORT.TABLE.PATIENT_DATE_OF_BIRTH.#NO'), width: 10 },
    { label: t('REPORT.TABLE.PATIENT_DATE_OF_BIRTH.CN'), width: 15 },
    { label: t('REPORT.TABLE.PATIENT_DATE_OF_BIRTH.SN'), width: 15 },
    { label: t('REPORT.TABLE.PATIENT_DATE_OF_BIRTH.FULL_NAME'), width: 25 },
    { label: t('REPORT.TABLE.PATIENT_DATE_OF_BIRTH.GENDER'), width: 12 },
    { label: t('REPORT.TABLE.PATIENT_DATE_OF_BIRTH.PATIENT_DATE_OF_BIRTH'), width: 15 },
    { label: t('REPORT.TABLE.PATIENT_DATE_OF_BIRTH.AGE'), width: 10 },
    { label: t('REPORT.TABLE.PATIENT_DATE_OF_BIRTH.PHONE'), width: 15 },
    { label: t('REPORT.TABLE.PATIENT_DATE_OF_BIRTH.TELEPHONE'), width: 15 },
    { label: t('REPORT.TABLE.PATIENT_DATE_OF_BIRTH.LINE'), width: 15 },
    { label: t('REPORT.TABLE.PATIENT_DATE_OF_BIRTH.EMAIL'), width: 20 },
    { label: t('REPORT.TABLE.PATIENT_DATE_OF_BIRTH.FACEBOOK'), width: 20 },
    { label: t('REPORT.TABLE.PATIENT_DATE_OF_BIRTH.LATEST_SERVICE_BRANCH'), width: 20 },
    { label: t('REPORT.TABLE.PATIENT_DATE_OF_BIRTH.LATEST_SERVICE_DATE'), width: 20 },
  ]

  // ลบคอลัมน์ SN ถ้า snType เป็น false
  if (!snType) {
    headCells.splice(2, 1)
  }

  // กำหนดความกว้างคอลัมน์
  sheet.columns = headCells.map(c => ({ width: c.width }))

  // เพิ่มหัวตาราง
  const headerRow = sheet.addRow(headCells.map(c => c.label))
  headerRow.font = { bold: true, size: 12 }
  headerRow.alignment = { horizontal: 'left', vertical: 'middle' }

  // ตรวจสอบว่ามีข้อมูลหรือไม่
  if (!data || data.length === 0) {
    const noDataRow = sheet.addRow([t('REPORT.TITLE.NO_DATA')])
    sheet.mergeCells(noDataRow.number, 1, noDataRow.number, headCells.length)
    noDataRow.font = { italic: true, color: { argb: 'FF000000' } }
    noDataRow.alignment = { horizontal: 'center' }
  } else {
    // เพิ่มข้อมูลแถว
    data.forEach((item, index) => {
      const row = [
        index + 1,
        item.cnNumber || '-',
        ...(snType ? [item.snCode || '-'] : []),
        item.fullName || '-',
        t(`REGISTER.FORM.GENDER_OPTION.${item.gender}`) || '-',
        item.dateOfBirthBuddhist || '-',
        item.age || '-',
        item.phone || '-',
        item.telephone || '-',
        item.line || '-',
        item.email || '-',
        item.facebook || '-',
        item.branchName || '-',
        item.createdAtBuddhist || '-',
      ]
      sheet.addRow(row)
    })
  }

  // สร้างไฟล์ Excel และดาวน์โหลด
  const buffer = await workbook.xlsx.writeBuffer()
  const timestamp = moment().format('YYYYMMDD_HHmmss')
  const blob = new Blob([buffer], { type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet' })
  saveAs(blob, `${t('REPORT.REPORT_NAME.PATIENT_DATE_OF_BIRTH')}_${timestamp}.xlsx`)
}

export default ExportToExcel 